/**
 * Phase 6.0 — Age verification gate (21+).
 *
 * Anonymous visitors only. Once accepted with "remember me", the flag persists
 * in localStorage indefinitely (until the user clears cookies/data).
 *
 * Logged-in users skip the gate entirely — their DOB was age-validated at
 * signup, so re-asking would be friction without compliance value.
 *
 * Flow:
 *   1. anonymous visitor lands on any route
 *   2. check localStorage for `ash_merch_age_verified === '1'`
 *   3. if missing → render full-screen blocking modal
 *   4. YES, I'M 21+ → write flag (or sessionStorage if "remember me" unchecked) → continue
 *   5. NO         → window.location = 'https://www.google.com'
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'ash_merch_age_verified';
const VALUE = '1';

function isAlreadyVerified() {
  try {
    return (
      window.localStorage.getItem(STORAGE_KEY) === VALUE ||
      window.sessionStorage.getItem(STORAGE_KEY) === VALUE
    );
  } catch (_e) {
    // Safari private mode etc — fall back to "not verified" so we re-ask.
    return false;
  }
}

function persistVerification(remember) {
  try {
    if (remember) {
      window.localStorage.setItem(STORAGE_KEY, VALUE);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, VALUE);
    }
  } catch (_e) {
    // Silent — failure to persist isn't worth blocking the user.
  }
}

export function AgeGate({ children }) {
  const { user, loading: authLoading } = useAuth();
  // Read storage synchronously on first paint so we don't flash the modal on
  // every refresh for already-verified visitors.
  const [verified, setVerified] = useState(isAlreadyVerified);
  const [remember, setRemember] = useState(true);

  // Logged-in users skip the gate (their DOB was age-validated at registration).
  // We auto-mark them verified so a future logout doesn't immediately re-prompt.
  useEffect(() => {
    if (user && !verified) {
      persistVerification(true);
      setVerified(true);
    }
  }, [user, verified]);

  // Still resolving auth — show nothing (avoids a flash of the gate on a
  // fast reload for an already-authed user).
  if (authLoading) return null;

  // Authed OR verified → render the app as normal.
  if (user || verified) return children;

  const handleYes = () => {
    persistVerification(remember);
    setVerified(true);
  };

  const handleNo = () => {
    window.location.href = 'https://www.google.com';
  };

  return (
    <>
      {/* Render the app underneath so the modal sits on top with a backdrop,
          matches the terminal aesthetic of the rest of the site. */}
      <div aria-hidden="true" style={{ filter: 'blur(8px)', pointerEvents: 'none' }}>
        {children}
      </div>
      <div
        className="fixed inset-0 z-[9999] bg-[#050003]/95 backdrop-blur-md flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-gate-title"
        data-testid="age-gate"
      >
        <div className="w-full max-w-md bg-[#0C0612] border border-[#39FF14] p-8 rounded-none shadow-[0_0_40px_rgba(57,255,20,0.25)]">
          <p className="text-[#39FF14] font-mono text-xs uppercase tracking-[0.2em] mb-2 text-center">
            {'>'} ASH&MERCH_
          </p>
          <h1
            id="age-gate-title"
            className="text-white text-2xl sm:text-3xl font-bold text-center my-6 font-mono"
            data-testid="age-gate-title"
          >
            Must be 21 or older<br />to enter
          </h1>

          <label
            className="flex items-center justify-center gap-2 mb-6 cursor-pointer select-none text-[#A3C7A8] font-mono text-xs"
            data-testid="age-gate-remember-label"
          >
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 accent-[#39FF14]"
              data-testid="age-gate-remember-checkbox"
            />
            <span>Remember me on this device</span>
          </label>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleYes}
              data-testid="age-gate-yes"
              className="flex-1 bg-[#39FF14] hover:bg-[#4AF626] text-[#050003] font-mono font-bold uppercase tracking-wider px-4 py-3 rounded-none transition-colors focus:outline-none focus:ring-2 focus:ring-[#39FF14] focus:ring-offset-2 focus:ring-offset-[#0C0612]"
            >
              Yes, I&apos;m 21+
            </button>
            <button
              type="button"
              onClick={handleNo}
              data-testid="age-gate-no"
              className="flex-1 border border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.08)] font-mono font-bold uppercase tracking-wider px-4 py-3 rounded-none transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF3B30] focus:ring-offset-2 focus:ring-offset-[#0C0612]"
            >
              No
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
